import { format, isValid, parse } from 'date-fns';
import { DayPicker as RdpDayPicker } from 'react-day-picker';
import { useDateFnsLocale } from '../../app/hooks/useDateFnsLocale';
import { capitalizeFirstLetter } from '@clickersphere/core/utils/capitalizeFirstLetter';
import useLanguageSelectorController from './LanguageSelector/useLanguageSelectorController';

interface IDayPickerProps {
  onSelect: (date: string) => void;
  selectedDate: string;
  disabled?: boolean;
}

export default function DayPicker({
  onSelect,
  selectedDate,
  disabled,
}: IDayPickerProps) {
  const { dateFormat } = useLanguageSelectorController();
  const locale = useDateFnsLocale();

  const today = new Date();
  const parsedSelected = selectedDate
    ? parse(selectedDate, dateFormat, new Date())
    : null;
  const hasValidSelection = parsedSelected !== null && isValid(parsedSelected);
  const effectiveSelected = hasValidSelection ? parsedSelected : today;

  return (
    <div className="text-text-primary">
      <RdpDayPicker
        disabled={disabled}
        mode="single"
        showOutsideDays
        onSelect={(date) => {
          if (!date) {
            if (!hasValidSelection) {
              onSelect(format(today, dateFormat));
            }
            return;
          }
          onSelect(format(date, dateFormat));
        }}
        selected={effectiveSelected}
        locale={locale}
        defaultMonth={effectiveSelected}
        classNames={{
          root: 'p-2 text-text-primary',
          months: 'relative',
          month_caption:
            'flex items-center justify-start font-medium text-text-primary',
          caption_label: 'text-text-primary',
          nav: 'absolute left-full -translate-x-16 top-0 flex gap-1',
          button_previous:
            'text-text-secondary hover:text-green-600 dark:text-text-secondary dark:hover:text-green-400 cursor-pointer p-1 rounded transition-colors',
          button_next:
            'text-text-secondary hover:text-green-600 dark:text-text-secondary dark:hover:text-green-400 cursor-pointer p-1 rounded transition-colors',
          chevron: 'fill-current',
          weekdays: 'text-text-tertiary',
          weekday:
            'text-text-tertiary dark:text-grey-500 text-xs font-normal uppercase pt-2 pb-1',
          week: 'text-center',
          day: 'p-0',
          day_button:
            'text-text-primary bg-transparent hover:bg-green-600 hover:text-grey-0 dark:text-text-primary dark:hover:bg-green-500 h-8 w-8 cursor-pointer rounded-full border-none p-1 transition-colors',
          today:
            '[&>button]:!font-semibold [&>button]:!text-green-600 dark:[&>button]:!text-green-400',
          selected:
            '[&>button]:!bg-green-600 [&>button]:!text-grey-0 [&>button:hover]:!bg-green-700 dark:[&>button]:!bg-green-500 dark:[&>button]:!text-grey-0 dark:[&>button:hover]:!bg-green-400',
          outside:
            '[&>button]:!text-grey-300 dark:[&>button]:!text-grey-600 [&>button:hover]:!text-grey-0',
          disabled:
            '[&>button]:!text-grey-300 dark:[&>button]:!text-grey-700 [&>button]:!cursor-not-allowed [&>button:hover]:!bg-transparent',
          hidden: 'invisible',
        }}
        formatters={{
          formatCaption: (dayPickerDate, options) =>
            capitalizeFirstLetter(format(dayPickerDate, 'LLLL yyyy', options)),
        }}
      />
    </div>
  );
}
